import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DateTimePicker from 'react-datetime-picker';
import DatePicker, { registerLocale } from 'react-datepicker';
import vi from 'date-fns/locale/vi';
import { Table, Pagination, Icon, Dropdown } from 'semantic-ui-react';
import ReportDetail from './ReportDetail';
import ExportCSV from './ExportCSV';

import styles from './Report.module.scss';

registerLocale('vi', vi);

const ReportContent = React.memo(
  ({
    isEditable,
    type,
    reports,
    currentPageEnergy,
    totalPages,
    currentPage,
    onPageChange,
    onChangeDate,
    onChangeDateInterval,
    onFetchReports,
  }) => {
    const [t] = useTranslation();
    const [openDevice, setOpenDevice] = useState(null);
    const [dateValue, setDateChange] = useState(new Date());
    const listItems = [];
    const [filterItem, setfilterItem] = useState(0);
    const listIntervals = [
      { key: 0, value: 5, text: '5 phút' },
      { key: 1, value: 15, text: '15 phút' },
      { key: 2, value: 30, text: '30 phút' },
      { key: 3, value: 60, text: '60 phút' },
    ];
    const [filterInterval, setFilterInterval] = useState(listIntervals[3].value);
    const local = localStorage.getItem('i18nextLng');

    const selectedInterval = useRef(filterInterval);
    const selectedItem = useRef(filterItem);
    const selectedDateValueforDayTab = useRef(dateValue);
    const selectedDateValueforMonthTab = useRef(dateValue);
    const selectedDateValueforYearTab = useRef(dateValue);
    let isIntegral = false;

    console.log('********************** ReportContent render');

    if (reports.reportItems) {
      reports.reportItems.forEach((reportItem, index) => {
        listItems.push({
          key: index,
          value: reportItem.itemName,
          text: reportItem.name,
        });
      });

      if (filterItem === 0 && listItems[0]) {
        const wattHourItem = listItems.find((item) => item.value === 'watt_hour');

        setfilterItem(wattHourItem?.value || listItems[0].value);
      }
    }

    const headerCells = [];
    let dateFilterShowFormat = 'dd/MM/yyyy';
    let dateFilterFormat = 'yMMdd';
    switch (type) {
      case 'daily': {
        const dataNum = (24 * 60) / filterInterval;
        const timeVal = new Date(dateValue);
        timeVal.setHours(0);
        timeVal.setMinutes(0);
        timeVal.setSeconds(0);
        for (let i = 0; i <= dataNum - 1; i += 1) {
          const time = format(timeVal, 'HH:mm');
          headerCells.push(time);
          timeVal.setMinutes(timeVal.getMinutes() + filterInterval);
        }
        break;
      }
      case 'monthly': {
        const yearValue = new Date(dateValue).getFullYear();
        const monthValue = new Date(dateValue).getMonth();
        const countDate = new Date(yearValue, monthValue + 1, 0).getDate();
        for (let i = 1; i <= countDate; i += 1) {
          if (i <= 9) {
            headerCells.push(`0${i}`);
          } else {
            headerCells.push(`${i}`);
          }
        }
        dateFilterShowFormat = 'MM/yyyy';
        dateFilterFormat = 'yMM';
        break;
      }
      case 'yearly': {
        for (let i = 1; i <= 12; i += 1) {
          if (i <= 9) {
            headerCells.push(`0${i}`);
          } else {
            headerCells.push(`${i}`);
          }
        }
        dateFilterShowFormat = 'yyyy';
        dateFilterFormat = 'y';
        break;
      }
      default:
    }

    reports.reportItems.forEach((item) => {
      if (filterItem === item.itemName) {
        if (item.isIntegral) {
          isIntegral = true;

          /* in case of integral item, add SUM column */
          headerCells.push(`SUM`);
        }
      }
    });

    const getFilterInterval = (event, { value }) => {
      setFilterInterval(value);
      selectedInterval.current = value;
      const datevalue = format(selectedDateValueforDayTab.current, dateFilterFormat);
      onChangeDateInterval(datevalue, selectedInterval);
    };

    const getFilterItem = (event, { value }) => {
      setfilterItem(value);
      selectedItem.current = value;
    };

    const onDateChange = useCallback(
      (value) => {
        if (value) {
          setDateChange(value);

          switch (type) {
            case 'monthly': {
              selectedDateValueforMonthTab.current = value;
              break;
            }
            case 'yearly': {
              selectedDateValueforYearTab.current = value;
              break;
            }
            default:
              selectedDateValueforDayTab.current = value;
          }

          const datevalue = format(value, dateFilterFormat);
          onChangeDate(datevalue, selectedInterval);
        }
      },
      [dateFilterFormat, onChangeDate, type],
    );

    const openedModalDetail = (id) => {
      setOpenDevice(id);
    };

    const closeModal = useCallback(() => {
      setOpenDevice(null);
    }, []);

    /* export xls data */
    const csvData = [];
    let keyIndex = 0;

    currentPageEnergy
      .filter((f) => f[filterItem])
      .forEach((item) => {
        const dataValues = item[filterItem] ? [...item[filterItem]] : [];
        const decimalPoint =
          reports?.reportItems?.find((items) => items.itemName === filterItem)?.decimalPoint || 0;
        const resultItem = {};
        resultItem.name = item.device.name;

        /* in case of integral item, add SUM column's data */
        if (isIntegral) {
          if (dataValues && dataValues.length > 0) {
            const sumVal = dataValues.reduce(
              (sum, current) => (current !== null ? sum + +current : sum),
              null,
            );
            dataValues.push(sumVal);
          }
        }

        if (dataValues && dataValues.length > 0) {
          keyIndex = 0;
          dataValues.forEach((value) => {
            resultItem[` ${headerCells[keyIndex]}`] =
              value !== null
                ? +parseFloat(value).toLocaleString('en-GB', {
                    minimumFractionDigits: item.decimalPoint,
                    maximumFractionDigits: decimalPoint,
                  })
                : '-';
            keyIndex += 1;
          });
        }
        csvData.push(resultItem);
      });

    useEffect(() => {
      if (reports?.reportItems?.length > 0) {
        if (selectedItem.current) {
          setfilterItem(selectedItem.current);
        }
      }
    }, [reports.reportItems, type]);

    useEffect(() => {
      switch (type) {
        case 'monthly': {
          setDateChange(selectedDateValueforMonthTab.current);
          break;
        }
        case 'yearly': {
          setDateChange(selectedDateValueforYearTab.current);
          break;
        }
        default:
          setDateChange(selectedDateValueforDayTab.current);
      }
      onFetchReports();
    }, [onFetchReports, type]);

    const renderDateTimePicker = (chartType) => {
      if (chartType === 'daily') {
        return (
          <DateTimePicker
            className={styles.dateTimePicker}
            locale={local}
            onChange={onDateChange}
            value={dateValue}
            format={dateFilterShowFormat}
          />
        );
      }
      if (chartType === 'monthly') {
        return (
          <DatePicker
            className={styles.datePicker}
            locale={local}
            dateFormat={dateFilterShowFormat}
            showMonthYearPicker
            selected={dateValue}
            onChange={onDateChange}
          />
        );
      }
      if (chartType === 'yearly') {
        return (
          <DatePicker
            className={styles.datePicker}
            locale={local}
            dateFormat={dateFilterShowFormat}
            showYearPicker
            yearItemNumber={8}
            selected={dateValue}
            onChange={onDateChange}
          />
        );
      }

      return '';
    };

    const renderTableBody = () => {
      return (
        <>
          {currentPageEnergy.map((item) => {
            const dataValues = item[filterItem] ? [...item[filterItem]] : [];
            const reportItem = reports.reportItems.find((items) => items.itemName === filterItem);
            const decimalPoint = reportItem?.decimalPoint || 0;
            let i = 0;

            /* in case of integral item, add SUM column's data */
            if (isIntegral) {
              if (dataValues && dataValues.length > 0) {
                const sumVal = dataValues.reduce(
                  (sum, current) => (current !== null ? sum + +current : sum),
                  null,
                );
                dataValues.push(sumVal);
              }
            }

            return (
              <Table.Row key={item.device.id}>
                <Table.Cell key={`name_ ${item.device.id}`}>
                  <div className={`flex items-center ${styles.reportNameWrapper}`}>
                    <button
                      type="button"
                      className={styles.reportName}
                      onClick={() => openedModalDetail(item.device.id)}
                    >
                      <span>{item.device.name}</span>
                    </button>
                  </div>
                  <ReportDetail
                    type={type}
                    energy={item}
                    opened={openDevice === item.device.id}
                    dateValue={dateValue}
                    closeModal={closeModal}
                  />
                </Table.Cell>
                {dataValues && dataValues.length > 0
                  ? dataValues.map((value) => {
                      i += 1;
                      return (
                        <Table.Cell key={i}>
                          <span className={styles.reportValue}>
                            {value !== null
                              ? parseFloat(value).toLocaleString('en-GB', {
                                  minimumFractionDigits: decimalPoint,
                                  maximumFractionDigits: decimalPoint,
                                })
                              : '-'}
                          </span>
                        </Table.Cell>
                      );
                    })
                  : headerCells.map(() => {
                      i += 1;
                      return (
                        <Table.Cell key={i}>
                          <span className={styles.reportValue}>-</span>
                        </Table.Cell>
                      );
                    })}
              </Table.Row>
            );
          })}
        </>
      );
    };

    return (
      <>
        {listItems.length > 0 && (
          <div className={styles.reportFilter}>
            <Dropdown
              className={styles.filterItems}
              defaultValue={filterItem}
              search
              selection
              options={listItems}
              onChange={getFilterItem}
            />
            {renderDateTimePicker(type)}
            {type === 'daily' && (
              <Dropdown
                className={styles.interval}
                defaultValue={filterInterval}
                search
                selection
                options={listIntervals}
                onChange={getFilterInterval}
              />
            )}
            {isEditable && (
              <div className={styles.actionExport}>
                <ExportCSV
                  csvData={csvData}
                  itemName={listItems.find((it) => it.value === filterItem)?.text || ''}
                  type={type}
                  dateValue={dateValue}
                  dateFilterFormat={dateFilterShowFormat}
                  headerCell={headerCells}
                />
              </div>
            )}
          </div>
        )}
        <div className={styles.tableWrapper}>
          <Table singleLine>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className="uppercase text-xs text-grey">
                  {t('common.devices.name')}
                </Table.HeaderCell>
                {headerCells.map((item) => (
                  <Table.HeaderCell className="uppercase text-xs text-grey" key={item}>
                    {item}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>{renderTableBody()}</Table.Body>
          </Table>
        </div>
        <div className={`text-right ${styles.paginationWrapper}`}>
          <Pagination
            activePage={currentPage}
            boundaryRange={0}
            onPageChange={onPageChange}
            size="mini"
            siblingRange={1}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
            totalPages={totalPages}
          />
        </div>
      </>
    );
  },
);

ReportContent.propTypes = {
  isEditable: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  reports: PropTypes.objectOf(PropTypes.any).isRequired,
  currentPageEnergy: PropTypes.arrayOf(PropTypes.any).isRequired,
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onChangeDate: PropTypes.func.isRequired,
  onChangeDateInterval: PropTypes.func.isRequired,
  onFetchReports: PropTypes.func.isRequired,
};

export default ReportContent;
